import { useState, useEffect, useRef, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';

// Building-tablet kiosk. Public route — no JWT required. Polls
// GET /api/clock/token for a rotating HMAC token and displays it as a QR.
// Staff scan it with their phone (already authed to the portal) which then
// POSTs to /api/me/clock-in with the token as proof of physical presence.
//
// Ported from bbc-booking. Same visual treatment (dark full-screen, big QR,
// circular countdown) so anyone who used the booking tablet recognizes it.

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function ClockKioskPage() {
  const [token, setToken] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [error, setError] = useState(false);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const retryRef = useRef(null);

  const fetchToken = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/clock/token`);
      if (!res.ok) throw new Error('Failed to fetch token');
      const data = await res.json();
      setToken(data.token);
      setTotalSeconds(data.expiresIn);
      setSecondsLeft(data.expiresIn);
      setError(false);

      if (retryRef.current) {
        clearTimeout(retryRef.current);
        retryRef.current = null;
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fetchToken, data.expiresIn * 1000);
    } catch {
      setError(true);
      if (retryRef.current) clearTimeout(retryRef.current);
      retryRef.current = setTimeout(fetchToken, 5000);
    }
  }, []);

  useEffect(() => {
    fetchToken();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (retryRef.current) clearTimeout(retryRef.current);
    };
  }, [fetchToken]);

  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setSecondsLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, []);

  useEffect(() => {
    const clockInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  const formattedTime = currentTime.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/New_York',
  });

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const progress = totalSeconds > 0 ? secondsLeft / totalSeconds : 0;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white px-4 select-none">
      <h1 className="text-2xl font-bold tracking-wide mb-2">BBC Time Clock</h1>
      <p className="text-sm text-gray-400 mb-8">Scan with your phone after logging into the portal</p>

      <div className="bg-white rounded-2xl p-5 mb-6">
        {token ? (
          // Encode the deep link that ClockPage understands: scanning with the
          // phone camera opens the portal /clock page with the token in the
          // query string. ClockPage strips the token + auto-fires the right
          // action.
          <QRCodeSVG
            value={`${window.location.origin}/clock?kt=${encodeURIComponent(token)}`}
            size={300}
            level="M"
            bgColor="#ffffff"
            fgColor="#111827"
          />
        ) : (
          <div className="w-[300px] h-[300px] flex items-center justify-center text-gray-400 text-lg">
            {error ? 'Reconnecting…' : 'Loading…'}
          </div>
        )}
      </div>

      <p className="text-xl font-medium mb-6">Scan to Clock In / Out</p>

      <div className="relative mb-6">
        <svg width="100" height="100" className="-rotate-90">
          <circle cx="50" cy="50" r={radius} fill="none" stroke="#374151" strokeWidth="6" />
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-[stroke-dashoffset] duration-1000 ease-linear"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-semibold">{secondsLeft}s</span>
        </div>
      </div>

      <p className="text-3xl font-mono tabular-nums mb-2">{formattedTime}</p>
      <p className="text-sm text-gray-400">Code refreshes automatically</p>

      {error && (
        <p className="mt-4 text-yellow-400 text-sm animate-pulse">
          Connection lost — retrying…
        </p>
      )}
    </div>
  );
}
